import { useEffect, useRef, useState } from 'react';
import { useLocation } from '@reach/router';
import { useGtag } from '~/context/GtagContext';
import convertObjectToSnakeCase from '~/utils/convert-object-to-snake-case';
import { serverIdentify } from './serverTrack';
import { useAnalytics } from '@chordcommerce/react-autonomy';
import useSiteMetadataQuery from '~/hooks/graphql/queries/use-site-metadata';

interface TrackData {
  [key: string]: any;
}

interface TrackEmailCapturedData {
  email: string;
  locationDetail: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
}

interface TrackClickCollectionData {
  locationDetail: string;
  slug: string;
  title: string;
}

interface TrackSubmitData {
  locationDetail: string;
  title: string;
  [key: string]: any;
}

interface TrackVideoData {
  locationDetail: string;
  title: string;
  time?: number;
  [key: string]: any;
}

const useCustomAnalytics = () => {
  const { site } = useSiteMetadataQuery() || { site: {} };
  const siteUrl = site.siteMetadata?.siteUrl;
  const gtagState = useGtag();
  const location = useLocation();
  const [locationTitle, setLocationTitle] = useState<string | undefined>();
  const { trackEmailCaptured: originalTrackEmail } = useAnalytics();

  useEffect(() => {
    if (!location) return;
    const locationPathArray = location.pathname.split('/');
    const filteredArray = locationPathArray.filter((path) => path !== '');
    const pageName = filteredArray?.[filteredArray.length - 1] || 'homepage';
    setLocationTitle(pageName);
  }, [location]);

  const scrollRef = useRef(false);

  const track = (event: string, data: TrackData = {}) => {
    const snakeCasedData = convertObjectToSnakeCase(data);
    if (/product/i.test(event) || /variant/i.test(event)) {
      window.analytics?.track(event, {
        locationHero: locationTitle,
        gtagSessionId: gtagState.gtagSessionId,
        gtagSessionNumber: gtagState.gtagSessionNumber,
        ...snakeCasedData,
        name: data?.name,
        variant: data?.variant,
      });
      return;
    }

    window.analytics?.track(event, {
      locationHero: locationTitle,
      gtagSessionId: gtagState.gtagSessionId,
      gtagSessionNumber: gtagState.gtagSessionNumber,
      ...snakeCasedData,
    });
  };

  const trackEmailCaptured = async ({
    email,
    locationDetail,
    firstName,
    lastName,
    dateOfBirth,
  }: TrackEmailCapturedData) => {
    try {
      const snakeCasedData = convertObjectToSnakeCase({ locationDetail, locationHero: locationTitle });
      originalTrackEmail({
        emailCapture: {
          email,
          component: snakeCasedData?.locationDetail,
          page: snakeCasedData.locationHero,
          website: siteUrl,
        },
      });
      await serverIdentify({ email, firstName, lastName, dateOfBirth, ...snakeCasedData });
    } catch (error) {
      throw error;
    }
  };

  const trackClickCollection = ({ locationDetail, slug, title }: TrackClickCollectionData) => {
    track('Collection Clicked', {
      title,
      brand: 'Omorpho',
      collection_id: `/${slug}/`,
      url: `https://www.omorpho.com/${slug}`,
      locationDetail,
    });
  };

  const trackScroll = () => {
    const scrollPercent = Math.round(
      ((document.documentElement.scrollTop || document.body.scrollTop) /
        (document.documentElement.scrollHeight - document.documentElement.clientHeight)) *
        100
    );
    if (scrollPercent >= 90 && !scrollRef.current) {
      scrollRef.current = true;
      track('Scroll', {
        scrollPercent,
      });
    }
  };

  const trackClick = (data: TrackData = {}) => {
    track('Click', {
      ...data,
    });
  };

  const trackClickLink = (data: TrackData = {}) => {
    track('Click Link', {
      ...data,
    });
  };

  const trackSubmit = ({ locationDetail, title }: TrackSubmitData, data: TrackData = {}) => {
    track('Submit', {
      locationDetail,
      title,
      ...data,
    });
  };

  const trackVideoPlay = ({ locationDetail, title }: TrackVideoData, data: TrackData = {}) => {
    track('Video Play', {
      type: 'video',
      locationDetail,
      title,
      ...data,
    });
  };

  const trackVideoPause = ({ locationDetail, title, time }: TrackVideoData, data: TrackData = {}) => {
    track('Video Pause', {
      type: 'video',
      locationDetail,
      title,
      time,
      ...data,
    });
  };

  const trackVideoSeek = ({ locationDetail, title, time }: TrackVideoData, data: TrackData = {}) => {
    track('Video Seek', {
      type: 'video',
      locationDetail,
      title,
      time,
      ...data,
    });
  };

  return {
    track,
    trackVideoPlay,
    trackVideoSeek,
    trackVideoPause,
    trackClickCollection,
    trackScroll,
    trackEmailCaptured,
    trackClick,
    trackClickLink,
    trackSubmit,
  };
};

export default useCustomAnalytics;
